import { effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { BroadcastChannelService } from '@shared/v2';
import {
  AuthActions,
  AuthService,
  AuthStorageService,
  AuthToken,
  OCC_USER_ID_CURRENT,
  UserIdService,
} from '@spartacus/core';
import { UserAccountConnector } from '@spartacus/user';
import { take, timeout } from 'rxjs/operators';

type TokenSyncMessage = { type: 'token-update'; token: AuthToken } | { type: 'logout'; accessTokenStoredAt: string };

@Injectable({ providedIn: 'root' })
export class TokenSyncService {
  private readonly authStorageService = inject(AuthStorageService);
  private readonly authService = inject(AuthService);
  private readonly userIdService = inject(UserIdService);
  private readonly store = inject(Store);
  private readonly userAccountConnector = inject(UserAccountConnector);
  private readonly broadcastChannel = inject(BroadcastChannelService<TokenSyncMessage>);

  private readonly currentToken = toSignal(this.authStorageService.getToken());

  private prevToken: AuthToken = {} as AuthToken;
  private skipNextBroadcast = false;
  private remoteLogout = false;

  constructor() {
    effect(() => {
      const token = this.currentToken();

      if (this.remoteLogout) {
        this.remoteLogout = false;
      } else if (this.skipNextBroadcast) {
        this.skipNextBroadcast = false;
      } else if (!token?.access_token && this.prevToken?.access_token) {
        this.broadcastChannel.postMessage({
          type: 'logout',
          accessTokenStoredAt: this.prevToken.access_token_stored_at ?? '',
        });
      } else if (token?.access_token && this.isNewerToken(token, this.prevToken)) {
        this.broadcastChannel.postMessage({ type: 'token-update', token });
      }

      this.prevToken = token ?? ({} as AuthToken);
    });

    this.broadcastChannel.messages$.subscribe((message) => {
      if (message.type === 'logout') {
        this.handleRemoteLogout(message.accessTokenStoredAt);

        return;
      }

      if (message.type === 'token-update') {
        this.handleRemoteTokenUpdate(message.token);
      }
    });
  }

  private handleRemoteTokenUpdate(remoteToken: AuthToken): void {
    const localToken = this.currentToken();

    if (!this.isNewerToken(remoteToken, localToken)) {
      return;
    }

    const wasAnonymous = !localToken?.access_token;

    this.skipNextBroadcast = true;
    this.authStorageService.setToken(remoteToken);

    if (!wasAnonymous) {
      return;
    }

    this.userIdService.setUserId(OCC_USER_ID_CURRENT);
    this.store.dispatch(new AuthActions.Login());

    this.userAccountConnector
      .get()
      .pipe(take(1), timeout(5000))
      .subscribe({
        error: () => this.reloadWhenVisible(),
      });
  }

  private handleRemoteLogout(remoteStoredAt: string): void {
    const localToken = this.currentToken();
    const localStoredAt = localToken?.access_token_stored_at ?? '';

    if (localToken?.access_token && localStoredAt <= remoteStoredAt) {
      this.remoteLogout = true;
      this.authService.coreLogout();
    }
  }

  private reloadWhenVisible(): void {
    if (document.visibilityState === 'visible') {
      window.location.reload();

      return;
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  private isNewerToken(a: AuthToken | undefined, b: AuthToken | undefined): boolean {
    return (a?.access_token_stored_at ?? '') > (b?.access_token_stored_at ?? '');
  }
}
